<template>
  <div class="w-full bg-white pb-16 tab-content">
    <div
      class="header-content px-4 py-8 mb-4 border-b flex item-center justify-between"
    >
      <h2>{{ $t('message.selfService.taskManager.address') }}</h2>
      <div class="flex justify-end items-center" v-if="shownSave">
        <zt-button
          color="dark-300"
          @click="cancel"
          class="mr-4"
          data-vs-button="cancel-address-edit"
          >
          {{ $t('message.selfService.linkCancel') }}
        </zt-button
        >
        <zt-button
          @click="save"
          :loader="isLoading"
          data-vs-button="confirm-address-edit"
          :disabled="isDisabled"
          >
          {{ $t('message.selfService.linkSaveChanged') }}
        </zt-button
        >
      </div>
    </div>
    <div class="px-4" :key="reRenderKey">
      <div v-if="shownAlert" class="mb-12">
        <zt-alert
          :color="statusAlert"
          close-icon
          filled
          @afterClosed="shownAlert = false"
          class="message px-3 py-3"
        >
          <div slot="custom-content">
            <div class="flex justify-start items-center">
              <zt-icon
                v-if="iconName"
                :name="iconName"
                class="icon mr-3"
              ></zt-icon>
              <p class="text-base">{{ messageAlert }}</p>
            </div>
          </div>
        </zt-alert>
      </div>
      <div class="flex">
        <div class="w-1/2 mr-4 custom-input mt-10 relative">
          <zt-input
            v-model="params.country"
            v-validate="{ regex: /^[a-zA-Z-À-ú-\s]+$/, min: 3, max: 150 }"
            name="country"
            class="w-full"
            :color="errors.has('country') ? 'danger' : 'primary'"
            :label="$t('message.selfService.taskManager.field.country')"
            data-vs-input="country"
            :disabled="readonly"
          />
          <div class="text-zt-danger" v-if="errors.has('country')">
            <span>
              {{ errors.first('country') }}
            </span>
          </div>
        </div>
        <div class="w-1/2 mr-4 mt-10 custom-input relative">
          <zt-input
            v-model="params.state"
            v-validate="{ min: 3, max: 50, regex: /^[a-zA-Z-À-ú-\s]+$/ }"
            name="state"
            class="w-full"
            :color="errors.has('state') ? 'danger' : 'primary'"
            :label="$t('message.selfService.taskManager.field.state')"
            data-vs-input="state"
            :disabled="readonly"
          />
          <div class="text-zt-danger" v-if="errors.has('state')">
            <span>
              {{ errors.first('state') }}
            </span>
          </div>
        </div>
      </div>
      <div class="flex">
        <div class="w-1/2 mt-10 mr-4 custom-input relative">
          <zt-input
            v-model="params.city"
            v-validate="{ min: 3, max: 50, regex: /^[a-zA-Z-À-ú-\s]+$/ }"
            name="city"
            class="w-full"
            :color="errors.has('city') ? 'danger' : 'primary'"
            :label="$t('message.selfService.taskManager.field.city')"
            data-vs-input="city"
            :disabled="readonly"
          />
          <div class="text-zt-danger" v-if="errors.has('city')">
            <span>
              {{ errors.first('city') }}
            </span>
          </div>
        </div>
        <div class="w-1/2 mr-4 custom-input mt-10 relative">
          <zt-input
            v-model="params.street"
            v-validate="{ min: 3, max: 100, regex: /^[0-9a-zA-Z-À-ú-\s]+$/ }"
            name="street"
            class="w-full"
            :color="errors.has('street') ? 'danger' : 'primary'"
            :label="$t('message.selfService.taskManager.field.street')"
            data-vs-input="street"
            :disabled="readonly"
          />
          <div class="text-zt-danger" v-if="errors.has('street')">
            <span>
              {{ errors.first('street') }}
            </span>
          </div>
        </div>
      </div>
      <div class="flex">
        <div class="w-1/2 mr-4 mt-10 custom-input relative">
          <zt-input
            v-model="params.number"
            v-validate="{ min: 1, max: 10, numeric: true }"
            name="number"
            class="w-full"
            :color="errors.has('number') ? 'danger' : 'primary'"
            :label="$t('message.selfService.taskManager.field.addressNumber')"
            data-vs-input="number"
            :disabled="readonly"
          />
          <div class="text-zt-danger" v-if="errors.has('number')">
            <span>
              {{ errors.first('number') }}
            </span>
          </div>
        </div>
        <div class="w-1/2 mr-4 custom-input mt-10 relative">
          <zt-input
            v-model="params.flat"
            v-validate="{ min: 1, max: 5, regex: /^[0-9a-zA-Z-À-ú-\s]+$/ }"
            name="flat"
            class="w-full"
            :color="errors.has('flat') ? 'danger' : 'primary'"
            :label="$t('message.selfService.taskManager.field.floor')"
            data-vs-input="flat"
            :disabled="readonly"
          />
          <div class="text-zt-danger" v-if="errors.has('flat')">
            <span>
              {{ errors.first('flat') }}
            </span>
          </div>
        </div>
      </div>
      <div class="flex">
        <div class="w-1/2 mr-4 mt-10 custom-input relative">
          <zt-input
            v-model="params.department"
            v-validate="{ min: 1, max: 5, regex: /^[0-9a-zA-Z-À-ú-\s]+$/ }"
            name="department"
            class="w-full"
            :color="errors.has('department') ? 'danger' : 'primary'"
            :label="$t('message.selfService.taskManager.field.department')"
            data-vs-input="department"
            :disabled="readonly"
          />
          <div class="text-zt-danger" v-if="errors.has('department')">
            <span>
              {{ errors.first('department') }}
            </span>
          </div>
        </div>
        <div class="w-1/2 mr-4 custom-input mt-10 relative">
          <zt-input
            v-model="params.neighborhood"
            v-validate="{ min: 1, max: 50, regex: /^[0-9a-zA-Z-À-ú-\s]+$/ }"
            name="neighborhood"
            class="w-full"
            :color="errors.has('neighborhood') ? 'danger' : 'primary'"
            :label="$t('message.selfService.taskManager.field.neighborHood')"
            data-vs-input="neighborhood"
            :disabled="readonly"
          />
          <div class="text-zt-danger" v-if="errors.has('neighborhood')">
            <span>
              {{ errors.first('neighborhood') }}
            </span>
          </div>
        </div>
      </div>
      <div class="flex">
        <div class="w-1/2 mr-4 mt-10 custom-input relative">
          <zt-input
            v-model="params.block"
            v-validate="{ min: 1, max: 50, regex: /^[0-9a-zA-Z-À-ú-\s]+$/ }"
            name="block"
            class="w-full"
            :color="errors.has('block') ? 'danger' : 'primary'"
            :label="$t('message.selfService.taskManager.field.blockStreet')"
            data-vs-input="block"
            :disabled="readonly"
          />
          <div class="text-zt-danger" v-if="errors.has('block')">
            <span>
              {{ errors.first('block') }}
            </span>
          </div>
        </div>
        <div class="w-1/2 mr-4 mt-10 custom-input relative">
          <zt-input
            v-model="params.type_domicile"
            v-validate="{ min: 1, max: 20, regex: /^[0-9a-zA-Z-À-ú-\s]+$/ }"
            name="type_domicile"
            class="w-full"
            :color="errors.has('type_domicile') ? 'danger' : 'primary'"
            :label="$t('message.selfService.taskManager.field.addressType')"
            data-vs-input="type-address"
            :disabled="true"
          />
          <div class="text-zt-danger" v-if="errors.has('type_domicile')">
            <span>
              {{ errors.first('type_domicile') }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { isEqual, isEmpty } from 'lodash'
import Employee from '@/apiClient/Employee'
import parseNull from '@/utils/parseNull'

const icons = {
  danger: 'exclamation-circle-solid',
  info: 'info-circle-solid'
}
export default {
  name: 'UserAddressInfo',
  props: {
    updateFn: {
      type: Function,
      default: () => {}
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      addressData: {},
      isLoading: false,
      params: {
        country: '',
        state: '',
        city: '',
        street: '',
        number: '',
        flat: '',
        department: '',
        neighborhood: '',
        block: '',
        type_domicile: 'Particular'
      },
      shownAlert: false,
      shownSave: false,
      statusAlert: 'info',
      messageAlert: '',
      cache: {},
      reRenderKey: 0,
      employee: '',
      isEmployeeCreated: true
    }
  },
  watch: {
    params: {
      handler(newParams) {
        this.shownSave = !isEqual(this.cache, newParams)
      },
      deep: true
    }
  },
  computed: {
    iconName() {
      return icons[this.statusAlert]
    },
    isDisabled() {
      return (
        this.errors.has('country') ||
        this.errors.has('state') ||
        this.errors.has('city') ||
        this.errors.has('street') ||
        this.errors.has('flat') ||
        this.errors.has('department') ||
        this.errors.has('neighborhood') ||
        this.errors.has('block') ||
        this.errors.has('type_domicile') ||
        this.errors.has('number')
      )
    }
  },
  created() {
    this.employee = new Employee()
    this.infoUpdate()
  },
  mounted() {
    this.getEmployeeAddressInfo()
  },
  methods: {
    cancel() {
      this.infoUpdate()
      this.errors.clear()
      this.shownAlert = false
      this.reRenderKey += 1
    },
    save() {
      this.isEmployeeCreated ? this.editAddress() : this.saveAddress()
    },
    async editAddress() {
      this.isLoading = true
      const payload = {
        country: this.params.country,
        state: this.params.state,
        city: this.params.city,
        street: this.params.street,
        number: this.params.number,
        flat: this.params.flat,
        department: this.params.department,
        neighborhood: this.params.neighborhood,
        block: this.params.block
      }
      try {
        await this.employee.updateAddressInfo(this.params.id, payload)
        this.shownSave = false
        this.dataContact = payload
        this.durationAlert = 5
        this.messageAlert = this.$t(`message.selfService.statusOk`)
        this.statusAlert = 'info'
        this.shownAlert = true
        this.shownSave = false
        this.isLoading = false
      } catch (error) {
        if (error.response.data) {
          this.messageAlert = this.$t(
            `message.selfService.errors.${error.response.data.codes[0].code}`
          )
          this.statusAlert = 'danger'
          this.shownAlert = true
        }
        this.isLoading = false
      }
    },
    async saveAddress() {
      this.isLoading = true
      const idEmployee = this.$route.params.id
      try {
        const payload = {
          country: this.params.country,
          state: this.params.state,
          city: this.params.city,
          street: this.params.street,
          number: this.params.number,
          flat: this.params.flat,
          department: this.params.department,
          neighborhood: this.params.neighborhood,
          block: this.params.block
        }
        await this.employee.createAddressInfo(idEmployee, payload)
        this.durationAlert = 5
        this.messageAlert = this.$t(`message.selfService.statusOk`)
        this.statusAlert = 'info'
        this.shownAlert = true
        this.shownSave = false
        this.isLoading = false
        await this.getEmployeeAddressInfo()
      } catch (error) {
        if (error.response.data) {
          this.messageAlert = this.$t(
            `message.selfService.errors.${error.response.data.codes[0].code}`
          )
          this.statusAlert = 'danger'
          this.shownAlert = true
        }
        this.isLoading = false
      }
    },
    infoUpdate() {
      if (!isEmpty(this.addressData)) {
        this.addressData['type_domicile'] = 'Particular'
        this.params = { ...this.addressData }
        this.cache = { ...this.addressData }
      }
    },
    checkErrors() {
      for (const [key] of Object.entries(this.params)) {
        if (key && this.errors.has(key)) return true
      }
      return false
    },
    async getEmployeeAddressInfo() {
      const idEmployee = this.$route.params.id
      this.isLoading = true
      try {
        const {
          data: { data }
        } = await this.employee.getAddressInfo(idEmployee)
        this.addressData = parseNull({ ...data })
        this.infoUpdate()
        this.reRenderKey += 1
        this.isLoading = false
        this.isEmployeeCreated = true
      } catch (error) {
        this.isEmployeeCreated = false
        this.isLoading = false
      }
    }
  }
}
</script>
<style scoped lang="scss">
.tab-content {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
  border-radius: 4px;
}
.header-content {
  h2 {
    font-weight: 500;
    font-size: 18px;
    line-height: 23px;
    color: #4f41a3;
  }
}
.custom-input {
  ::v-deep {
    .zt-label {
      margin-bottom: 0 !important;
    }
    .zt-label.zt-label-active {
      background: #ffff !important;
      padding: 2px !important;
    }
    .zt-input {
      font-size: 1.125rem;
    }
  }
}
.select {
  height: 50px;
  width: 100%;
  border-radius: 5px;
  padding: 8px;
  select {
    background: #fff;
  }
  label {
    height: 20px;
    line-height: 20px;
    top: 50%;
    background: #fff;
    padding: 0 2px;
    transform: scale(1) translate(0, -50%);
    transform-origin: top left;
    transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  }
  .focus {
    top: 0;
    transform: scale(0.8) translate(0, -50%);
  }
}
::v-deep .zt-input-disabled .zt-input-slot .zt-label[data-v-aea3bcaa] {
  color: #969696;
}
</style>
